import { useState } from "react";
import { toast } from "sonner";
import { eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Banknote, CreditCard, Users, UtensilsCrossed } from "lucide-react";

const lineTotal = (i) => (i.price + (i.surcharge || 0)) * i.qty;

export const DividiConto = ({ order, pay }) => {
  const { t } = useI18n();
  const [mode, setMode] = useState("persone");
  const [split, setSplit] = useState(order.covers || 2);
  const [payers, setPayers] = useState(1);
  const [sel, setSel] = useState({});

  const unpaid = order.items.filter((i) => !i.paid);
  const paidItems = order.items.filter((i) => i.paid);
  const perHead = order.remaining / (Number(split) || 1);
  const quota = Math.min(order.remaining, perHead * (Number(payers) || 1));
  const selIds = unpaid.filter((i) => sel[i.id]).map((i) => i.id);
  const itemsTotal = unpaid.filter((i) => sel[i.id]).reduce((s, i) => s + lineTotal(i), 0);
  const coverShare = order.covers ? (order.cover_total / order.covers) * (Number(payers) || 1) : 0;

  const payItems = async (method) => {
    if (!selIds.length) return toast.error(t("b.pickDish"));
    const amount = Math.min(order.remaining, Number(itemsTotal.toFixed(2)));
    await pay(method, amount, { item_ids: selIds, note: `${selIds.length} ${t("b.byDishes")}` });
    setSel({});
  };

  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid="dividi-conto">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{t("b.split")}</p>

      <div className="mt-2 flex gap-2">
        <Button size="sm" variant={mode === "persone" ? "default" : "outline"} onClick={() => setMode("persone")} data-testid="split-mode-persone">
          <Users className="mr-1.5 h-3.5 w-3.5" /> {t("b.byPeople")}
        </Button>
        <Button size="sm" variant={mode === "piatti" ? "default" : "outline"} onClick={() => setMode("piatti")} data-testid="split-mode-piatti">
          <UtensilsCrossed className="mr-1.5 h-3.5 w-3.5" /> {t("b.byDishes")}
        </Button>
      </div>

      {mode === "persone" ? (
        <div className="mt-3 space-y-2" data-testid="split-persone-panel">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-slate-500">{t("b.in")}</span>
            <Input className="h-9 w-16" type="number" min="1" value={split} onChange={(e) => setSplit(e.target.value)} data-testid="split-input" />
            <span className="text-slate-500">{t("b.people")} →</span>
            <span className="font-num font-bold" data-testid="split-quota">{eur(perHead)}</span>
            <span className="text-slate-500">{t("b.each")}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-slate-500">{t("b.payFor")}</span>
            <Input className="h-9 w-16" type="number" min="1" value={payers} onChange={(e) => setPayers(e.target.value)} data-testid="payers-input" />
            <span className="text-slate-500">{t("b.people")} →</span>
            <span className="font-num font-bold" data-testid="split-amount">{eur(quota)}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button className="h-11" data-testid="split-pay-cash-btn" onClick={() => pay("contanti", Number(quota.toFixed(2)), { note: `${t("b.quota")} ${payers}/${split}` })}>
              <Banknote className="mr-1 h-4 w-4" /> {t("b.cash")}
            </Button>
            <Button className="h-11" variant="outline" data-testid="split-pay-card-btn" onClick={() => pay("carta", Number(quota.toFixed(2)), { note: `${t("b.quota")} ${payers}/${split}` })}>
              <CreditCard className="mr-1 h-4 w-4" /> {t("b.card")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-2" data-testid="split-piatti-panel">
          {unpaid.length === 0 && <p className="text-sm text-slate-500">{t("b.allPaid")}</p>}
          {unpaid.map((i) => (
            <label key={i.id} className="flex items-center gap-2 text-sm" data-testid={`split-item-${i.id}`}>
              <Checkbox checked={!!sel[i.id]} onCheckedChange={(v) => setSel({ ...sel, [i.id]: !!v })} data-testid={`split-check-${i.id}`} />
              <span className="min-w-0 flex-1 truncate">
                <span className="font-num">{i.qty}×</span> {i.name}
              </span>
              <span className="font-num shrink-0 font-semibold">{eur(lineTotal(i))}</span>
            </label>
          ))}
          {paidItems.length > 0 && (
            <div className="rounded bg-slate-50 p-2" data-testid="split-paid-items">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{t("b.alreadyPaid")}</p>
              {paidItems.map((i) => (
                <p key={i.id} className="text-xs text-slate-400 line-through">
                  {i.qty}× {i.name}
                </p>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-sm">
            <span className="text-slate-500">{t("b.selected", { n: selIds.length })}</span>
            <span className="font-num text-lg font-bold" data-testid="split-items-total">{eur(itemsTotal)}</span>
          </div>
          <p className="text-[11px] text-slate-400">{t("b.coverNote", { amount: eur(coverShare), n: payers })}</p>
          <div className="grid grid-cols-2 gap-2">
            <Button className="h-11" disabled={!selIds.length} data-testid="pay-items-cash-btn" onClick={() => payItems("contanti")}>
              <Banknote className="mr-1 h-4 w-4" /> {t("b.cash")}
            </Button>
            <Button className="h-11" variant="outline" disabled={!selIds.length} data-testid="pay-items-card-btn" onClick={() => payItems("carta")}>
              <CreditCard className="mr-1 h-4 w-4" /> {t("b.card")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
